import { create } from 'zustand';
import { nanoid } from 'nanoid/non-secure';
import { createBackend, type Backend } from '@/backend/createBackend';
import { SqliteRecordStore } from '@/backend/stores/SqliteRecordStore';
import { MockBackend } from '@/backend/MockBackend';
import { useAuthStore } from '@/store/authStore';
import { useDeploymentStore } from '@/store/deploymentStore';
import { CognitoService } from '@/auth/CognitoService';
import { isDemoAppId } from '@/demos/registry';
import type { BackendClient, PlatformBackend } from '@/backend/types';

// Note: opens the SQLite db at import time via @/storage/sqlite. The schema is
// created by initDatabase() in the app root layout before any feature screen
// that imports this store renders.
const store = new SqliteRecordStore();

const mockDeps = {
  store,
  getUser: () => {
    const user = useAuthStore.getState().user;
    return user ? { userId: user.id } : null;
  },
  idGen: () => nanoid(),
  now: () => new Date().toISOString(),
};

// Bundled demos are always served locally (SQLite), regardless of the deployment
// mode, so their data never touches the server.
const demoBackend = new MockBackend(mockDeps);

function buildBackend(): Backend {
  const config = useDeploymentStore.getState().config;
  return createBackend({
    ...mockDeps,
    mode: config?.mode ?? 'mock',
    aws: config ? { baseUrl: config.apiBaseUrl, getToken: () => CognitoService.getAccessTokenSync() } : undefined,
  });
}

interface BackendStoreState {
  getClient: (appId: string) => BackendClient;
  platform: PlatformBackend;
  rebuild: () => void;
}

export const useBackendStore = create<BackendStoreState>((set) => {
  let backend = buildBackend();
  return {
    getClient: (appId) => (isDemoAppId(appId) ? demoBackend.getClient(appId) : backend.getClient(appId)),
    platform: backend.platform,
    rebuild: () => {
      backend = buildBackend();
      set({ getClient: (id) => (isDemoAppId(id) ? demoBackend.getClient(id) : backend.getClient(id)), platform: backend.platform });
    },
  };
});
